// Shared API types. The backend wraps every response in
// { success, data, error, message }; the base query unwraps it and reads the
// body untyped. Failure shapes (confirmed 2026-10-23): a 2xx `success:false`
// carries `error` as an object or null — a structured object may hold more
// than `msg` (e.g. the ended-workflow rejection's `{flowState}`); an
// HTTP-error body carries a string or a string ARRAY (validation errors) as
// well (see envelopeErrorMsg). `message` is success copy only — present but
// "" on every failure, never read.

export type TokenType =
  | 'authToken'
  | 'loginToken'
  | 'refreshToken'
  | 'loginRefreshToken'
  | 'staticToken'
  // Short-lived OTP-verification token; used only by reset-password.
  | 'validationKey'
  | 'external';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** A backend route descriptor — path, method, and which token to attach, all
 *  in one place (see api/routes.ts). Endpoints just spread this into the query. */
export interface RouteDef {
  url: string;
  method: HttpMethod;
  tokenType: TokenType;
}

/** Normalized error shape carried through the base query and thrown by the client. */
export interface ApiErrorShape {
  status?: number;
  /** Typed as string for the many `?.msg ?? fallback` toast sites, but a
   *  string ARRAY arrives verbatim from the HTTP-error path (client.ts) when
   *  the body sent one (validation errors) — user decision 2026-10-23, so a
   *  screen can list every message. Read it as unknown where arrays matter
   *  (`utils/apiErrorToast` shows the first item; a component wanting more
   *  handles `msg` in its own catch). */
  msg: string;
  /** The envelope's `error` object, passed through for feature-level branching
   *  (null is normalized to undefined). Narrow it with a typed guard beside the
   *  owning contract — e.g. submissionsApi's workflowFlowStateOf. */
  data?: unknown;
}

export class ApiError extends Error implements ApiErrorShape {
  status?: number;
  msg: string;
  constructor(msg: string, status?: number) {
    super(msg);
    this.name = 'ApiError';
    this.msg = msg;
    this.status = status;
  }
}

const nonBlank = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v : undefined;

/** The copy out of an HTTP-error body's `error` (client.ts): a non-blank
 *  string as is, a non-empty array AS IS (never collapsed or filtered —
 *  components decide how to show a list), or an object's non-blank `msg`;
 *  undefined when the shape carries none (null, "", [], an object without
 *  `msg`). Callers chain their own fallback after it. */
export const envelopeErrorMsg = (error: unknown): string | string[] | undefined => {
  if (Array.isArray(error)) return error.length ? error : undefined;
  if (error && typeof error === 'object') return nonBlank((error as { msg?: unknown }).msg);
  return nonBlank(error);
};
